import { action, computed, extendObservable } from 'mobx';
import uuid from 'node-uuid';
import { snakeCase, camelCase } from '@ridi/object-case-converter';
import Mvp from '../models/Mvp';
import VirtualBmcSection from '../models/VirtualBmcSection';
import axios from '../../../libs/axios_csrf';
import { safeCopy } from '../../common_components/serialization/common';

export default class MvpStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    extendObservable(this, {
      mvps: [],
      showNewModal: false
    });
    this.lastMvpNumber = null;
  }

  loadData(valueProposition) {
    const { mvps } = valueProposition;
    for (const mvp of mvps) {
      const newMvp = Mvp.deserialize(mvp);
      newMvp.store = this;
      newMvp.id = mvp.id;
      this.addBmc(newMvp);
      this.mvps.push(newMvp);
    }
    this.lastMvpNumber = valueProposition.lastMvpNumber;
  }

  addBmc(mvp) {
    const virtualBmcSection = new VirtualBmcSection(mvp);
    mvp.virtualBmcSection = virtualBmcSection;
  }

  getById(id) {
    return this.getMvp(id);
  }

  getMvp(mvpId) {
    return this.mvps.find(el => el.id == mvpId);
  }

  getMvpByMvpNumber(value) {
    return this.mvps.find(el => el.mvpNumber == value);
  }

  optionsForMvpsSelect(mvp) {
    return this.mvps
      .filter(el => el.canBePriorMvpOf(mvp) && el.isPersisted)
      .map(m => {
        return { value: m.id, label: m.name };
      });
  }

  @computed
  get allOptionsForMvpsSelect() {
    return this.mvps
      .filter(el => el.isPersisted)
      .map(m => {
        return { value: m.id, label: m.name };
      });
  }

  get currentMvpId() {
    return this.rootStore.currentMvpId;
  }

  get valueProposition() {
    return this.rootStore.valuePropositionStore.valueProposition;
  }

  getMvpFeaturesByMvp(mvpId) {
    return this.rootStore.getMvpFeaturesByMvp(mvpId);
  }

  update(mvp) {
    this.updateFrontend(mvp);
    this.updateBackend(mvp);
  }

  updateFrontend(mvp) {
    // Update all mvp sections in frontend
    mvp.sections.forEach(section => section.updateFrontend());
    // this.rootStore.networkStore.updateObject(mvp.virtualBmcSection);
    this.rootStore.networkStore.updateObject(mvp);
    this.rootStore.timelineStore.updateObject(mvp);
  }

  getSectionsByMvp(id) {
    return this.rootStore.getSectionsByMvp(id);
  }

  create(object) {
    const mvp = new Mvp(this, uuid.v4(), false);
    mvp.updateWithoutPersistance(object);
    this.lastMvpNumber++;
    mvp.name = `MVP ${this.lastMvpNumber}`;
    this.mvps.push(mvp);
    this.rootStore.networkStore.network.addNewMvp(mvp);
    this.rootStore.timelineStore.addObject(mvp);
    this.createBackend(mvp);
  }

  destroy(mvp) {
    this.destroyFrontend(mvp);
    this.destroyBackend(mvp);
  }

  destroyFrontend(mvp) {
    this.rootStore.handleElementDestroy();
    this.rootStore.networkStore.network.removeObject(mvp);
    this.mvps.remove(mvp);
    this.removeFromPriorMvpIds(mvp);
    this.rootStore.networkStore.network.reinitEdges();
  }

  // Remove all priorMvpId relations to the given mvp.
  removeFromPriorMvpIds(mvp) {
    mvp.nextMvps.forEach(el => {
      el.removePriorMvpId(mvp.id);
      el.addPriorMvpIds(mvp.priorMvpIds);
      el.persist();
    });
  }

  newObject() {
    const mvp = new Mvp(this);
    const { selectedMvp } = this.rootStore;
    if (selectedMvp && selectedMvp.isPersisted) {
      mvp.addPriorMvpIds([selectedMvp.id]);
    }
    return mvp;
  }

  createSectionsFrontend(mvpObj) {
    this.rootStore.createSectionsFrontend(mvpObj);
  }

  // //////////// BACKEND CONNECTIONS /////////////////////
  updateBackend(mvp) {
    axios
      .patch(`/mvps/${mvp.id}.json`, {
        mvp: snakeCase(mvp.serialize())
      })
      .then(response => {
        this.rootStore.generateAlert({
          type: 'success',
          timeout: 1500,
          headline: 'alert.update.headline',
          message: 'alert.update.messages.success',
          intlValues: { name: mvp.name }
        });
      })
      .catch(err => {
        let errors;
        try {
          errors = err.response.data.errors.join('\n');
        } catch (e) {
          errors = '';
        }
        this.rootStore.generateAlert({
          type: 'warning',
          timeout: 5000,
          headline: 'alert.update.headline',
          message: 'alert.update.messages.error',
          intlValues: { err: mvp.name }
        });
      });
  }

  createBackend(mvp) {
    axios
      .post('/mvps.json', {
        mvp: {
          ...snakeCase(mvp.serialize()),
          ...{ value_proposition_id: this.rootStore.valuePropositionId }
        }
      })
      .then(response => {
        const mvpObj = camelCase(response.data.mvp, true);
        const newMvp = Mvp.deserialize(mvpObj);
        safeCopy(mvp, newMvp);
        const oldId = mvp.id;
        mvp.id = mvpObj.id;
        mvp.creationId = oldId;
        mvp.isPersisted = true;
        this.addBmc(mvp);
        this.createSectionsFrontend(mvpObj);
        this.rootStore.networkStore.network.updateMvpAfterPersistance(mvp);
        this.rootStore.timelineStore.updateObject(mvp);
        this.rootStore.generateAlert({
          type: 'success',
          timeout: 5000,
          message: 'mvp_create_success'
        });
      })
      .catch(function(error) {
        console.log(error);
      });
  }

  destroyBackend(mvp) {
    axios
      .delete(`/mvps/${mvp.id}.json`)
      .then(response => {})
      .catch(errors => {
        console.log(errors);
      });
  }
  // ///////////////////////////////////////////////////
}
